// QRCodeExportUtils.js
// Save this as a separate file in your utils folder

import jsPDF from "jspdf";

const QRCodeExportUtils = {
  
  // Create a styled canvas with QR code, device name, and border
  createStyledQRCanvas: (qrCodeRef, deviceName, options = {}) => {
    return new Promise((resolve) => {
      const {
        canvasWidth = 420,
        canvasHeight = 470,
        padding = 50,
        borderWidth = 8,
        borderRadius = 20,
        borderColor = '#334155',
        backgroundColor = '#ffffff',
        textColor = '#1f2937',
        subtitleColor = '#059669',
        fontSize = 18,
        fontFamily = 'Arial, sans-serif'
      } = options;

      const svg = qrCodeRef.current.querySelector("svg");
      if (!svg) {
        console.error('QR Code SVG not found');
        return;
      }
      
      const svgData = new XMLSerializer().serializeToString(svg);
      
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      
      // Set canvas size
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      
      const img = new Image();
      img.onload = () => {
        // Fill background with rounded rectangle
        ctx.fillStyle = backgroundColor;
        ctx.beginPath();
        ctx.roundRect(0, 0, canvasWidth, canvasHeight, borderRadius);
        ctx.fill();
        
        // Draw thick rounded border
        ctx.strokeStyle = borderColor;
        ctx.lineWidth = borderWidth;
        ctx.beginPath();
        ctx.roundRect(borderWidth/2, borderWidth/2, canvasWidth - borderWidth, canvasHeight - borderWidth, borderRadius);
        ctx.stroke();
        
        // Add subtle inner shadow effect
        const gradient = ctx.createRadialGradient(canvasWidth/2, canvasHeight/2, 0, canvasWidth/2, canvasHeight/2, Math.max(canvasWidth, canvasHeight)/2);
        gradient.addColorStop(0, 'rgba(0,0,0,0)');
        gradient.addColorStop(1, 'rgba(0,0,0,0.03)');
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.roundRect(borderWidth, borderWidth, canvasWidth - (borderWidth*2), canvasHeight - (borderWidth*2), borderRadius - borderWidth);
        ctx.fill();
        
        // Calculate QR code position (centered, leaving space for text)
        const qrSize = 260;
        const qrX = (canvasWidth - qrSize) / 2;
        const qrY = padding + 10;
        
        // Draw QR code
        ctx.drawImage(img, qrX, qrY, qrSize, qrSize);
        
        // Draw device name
        ctx.fillStyle = textColor;
        ctx.font = `bold ${fontSize}px ${fontFamily}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        const textY = qrY + qrSize + 35;
        ctx.fillText(deviceName, canvasWidth / 2, textY);
        
        // Add "Share Your Feedback" subtitle
        const feedbackFontSize = fontSize + 14; // make text larger
        ctx.font = `bold ${feedbackFontSize}px ${fontFamily}`;
        ctx.fillStyle = subtitleColor;
        ctx.fillText('Share Your Feedback', canvasWidth / 2, textY + 35);
        
        resolve(canvas);
      };
      
      img.onerror = (error) => {
        console.error('Error loading QR code image:', error);
        resolve(null);
      };
      
      img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
    });
  },

  // Download as PNG
  downloadPNG: async (qrCodeRef, deviceName) => {
    try {
      const canvas = await QRCodeExportUtils.createStyledQRCanvas(qrCodeRef, deviceName);
      if (!canvas) {
        throw new Error('Failed to create canvas');
      }
      
      const pngFile = canvas.toDataURL("image/png", 1.0);
      
      const downloadLink = document.createElement("a");
      downloadLink.download = `${deviceName.replace(/[^a-z0-9]/gi, '_')}-qr-code.png`;
      downloadLink.href = pngFile;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    } catch (error) {
      console.error('Error downloading PNG:', error);
      alert('Error downloading QR code. Please try again.');
    }
  },

  // Download as PDF
  downloadPDF: async (qrCodeRef, deviceName) => {
    try {
      const canvas = await QRCodeExportUtils.createStyledQRCanvas(qrCodeRef, deviceName);
      if (!canvas) {
        throw new Error('Failed to create canvas');
      }
      
      const pngFile = canvas.toDataURL("image/png", 1.0);
      
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      // Calculate centered position on A4
      const pdfWidth = 210; // A4 width in mm
      const pdfHeight = 297; // A4 height in mm
      const imgWidth = 100; // Desired image width in mm
      const imgHeight = 112.5; // Maintain aspect ratio
      const x = (pdfWidth - imgWidth) / 2;
      const y = (pdfHeight - imgHeight) / 2;
      
      pdf.addImage(pngFile, 'PNG', x, y, imgWidth, imgHeight);
      pdf.save(`${deviceName.replace(/[^a-z0-9]/gi, '_')}-qr-code.pdf`);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Error creating PDF. Please try again.');
    }
  },

  // Print only QR code
  printQRCode: async (qrCodeRef, deviceName) => {
    try {
      const canvas = await QRCodeExportUtils.createStyledQRCanvas(qrCodeRef, deviceName);
      if (!canvas) {
        throw new Error('Failed to create canvas');
      }
      
      const dataUrl = canvas.toDataURL("image/png", 1.0);
      
      // Create a new window for printing
      const printWindow = window.open('', '_blank', 'width=600,height=700');
      
      if (!printWindow) {
        alert('Please allow popups to print the QR code.');
        return;
      }
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>QR Code - ${deviceName}</title>
            <style>
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              body {
                font-family: Arial, sans-serif;
                background: white;
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: 100vh;
                padding: 20px;
              }
              .qr-print-container {
                text-align: center;
                max-width: 400px;
              }
              .qr-image {
                width: 100%;
                max-width: 400px;
                height: auto;
                display: block;
                margin: 0 auto;
              }
              @media print {
                body {
                  margin: 0;
                  padding: 0;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  min-height: 100vh;
                }
                .qr-print-container {
                  page-break-inside: avoid;
                }
              }
              @page {
                margin: 1cm;
                size: A4;
              }
            </style>
          </head>
          <body>
            <div class="qr-print-container">
              <img src="${dataUrl}" alt="QR Code for ${deviceName}" class="qr-image" />
            </div>
          </body>
        </html>
      `);
      
      printWindow.document.close();
      
      // Wait for image to load then print
      const img = printWindow.document.querySelector('.qr-image');
      img.onload = () => {
        setTimeout(() => {
          printWindow.print();
          // Optional: close window after printing (user might want to keep it open)
          // printWindow.close();
        }, 500);
      };
      
    } catch (error) {
      console.error('Error printing QR code:', error);
      alert('Error preparing QR code for printing. Please try again.');
    }
  }
};

export default QRCodeExportUtils;